import { useState, useEffect, useRef } from "react";

import { Panel } from "primereact/panel";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Skeleton } from "primereact/skeleton";

import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { DEFAULT_ROW_COUNT } from "sj-ui-toolkit/src/constants/tableRowsCounts";

import { getLevelsList, setLevelSettings } from "sj-ui-toolkit/src/api/admin/settings";
import type { LevelData, LevelsListData } from "@/api/settings";
import { PathPreview } from "./PathPreview";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LEVEL_THEME_OPTIONS = [
  { label: "Belts", value: "belt" },
  { label: "Climbs", value: "climb" },
  { label: "Levels", value: "level" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDefaultVariant = (rankName: string): string => {
  switch (rankName) {
    case "white":
      return "mauve";
    case "yellow":
      return "indigo";
    case "green":
      return "teal";
    case "brown":
    case "black":
      return "blue";
    default:
      return "teal";
  }
};

const getDefaultMapId = (rankName: string): number => {
  switch (rankName) {
    case "white":
      return 1;
    case "yellow":
      return 2;
    case "green":
      return 3;
    case "brown":
    case "black":
      return 4;
    default:
      return 1;
  }
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LevelSettingsProps {
  currentTheme?: string;
  updateTheme?: (theme: string) => void;
  showToast?: (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string,
    life?: number
  ) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LevelSettings = ({
  currentTheme,
  updateTheme = () => {},
  showToast = () => {},
}: LevelSettingsProps) => {
  const [selectedTheme, setSelectedTheme] = useState(currentTheme || "belt");
  const [allLevelsData, setAllLevelsData] = useState<LevelsListData | null>(null);
  const [currentLevel, setCurrentLevel] = useState<LevelData[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<LevelData | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<Partial<LevelData>>({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadLevels = async () => {
      try {
        const levelsData = (await getLevelsList()) as LevelsListData | null;
        setAllLevelsData(levelsData);
      } catch (error) {
        console.error("Failed to load levels", error);
        showToast("error", "Error", "Failed to load levels. Please try again.");
      }
    };

    loadLevels();
  }, []);

  useEffect(() => {
    if (currentTheme !== selectedTheme) {
      setSelectedTheme(currentTheme || "belt");
    }
  }, [currentTheme]);

  useEffect(() => {
    if (allLevelsData && selectedTheme) {
      const themeData = allLevelsData[selectedTheme as keyof LevelsListData] || [];
      const enrichedData = themeData.map(level => ({
        ...level,
        variant: level.variant || getDefaultVariant(level.rankName),
        mapId: level.mapId || getDefaultMapId(level.rankName),
      }));

      setCurrentLevel(enrichedData);

      if (selectedLevel) {
        const newSelected = enrichedData.find(l => l.rankName === selectedLevel.rankName);
        setSelectedLevel(newSelected || enrichedData[0] || null);
      } else {
        setSelectedLevel(enrichedData[0] || null);
      }
    }
  }, [allLevelsData, selectedTheme]);

  if (!allLevelsData) {
    const skeletonRows = Array.from({ length: DEFAULT_ROW_COUNT });
    return (
      <Panel header="Level Settings" pt={{ content: { className: "bg-transparent" } }}>
        <div
          className={C(themeClasses.bgElevation2, "flex flex-col gap-6 p-6 rounded-md shadow-md")}
        >
          <div className="flex items-center gap-4">
            <Skeleton width="80px" height="1rem" />
            <Skeleton width="200px" height="2.25rem" />
          </div>
          <div
            className={C(
              "border rounded-md overflow-hidden",
              themeClasses.borderSurfaceBorder,
              themeClasses.bgElevation2
            )}
          >
            <div
              className={C(
                "flex border-b-2 font-semibold text-color",
                themeClasses.bgElevation1,
                themeClasses.borderSurfaceBorder
              )}
            >
              <div className="flex items-center p-4" style={{ width: "30%" }}>
                <Skeleton width="4rem" height="1rem" />
              </div>
              <div className="flex items-center p-4" style={{ width: "50%" }}>
                <Skeleton width="6rem" height="1rem" />
              </div>
              <div className="flex items-center justify-center p-4" style={{ width: "20%" }}>
                <Skeleton width="4rem" height="1rem" />
              </div>
            </div>
            <div>
              {skeletonRows.map((_, i) => (
                <div
                  key={i}
                  className={C(
                    "flex border-b border-surface-border",
                    i % 2 === 1 ? themeClasses.bgElevation3 : themeClasses.bgElevation2
                  )}
                >
                  <div className="flex items-center p-4" style={{ width: "30%" }}>
                    <Skeleton width="8rem" height="1.25rem" />
                  </div>
                  <div className="flex items-center p-4" style={{ width: "50%" }}>
                    <Skeleton width="14rem" height="1.25rem" />
                  </div>
                  <div
                    className="flex items-center justify-center gap-2 p-4"
                    style={{ width: "20%" }}
                  >
                    <Skeleton width="2rem" height="2rem" borderRadius="6px" />
                    <Skeleton width="2rem" height="2rem" borderRadius="6px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Panel>
    );
  }

  const handleThemeChange = async (e: { value: string }) => {
    const newTheme = e.value;
    setSelectedTheme(newTheme);
    updateTheme(newTheme);
  };

  const handleEdit = (level: LevelData) => {
    setEditingRowId(level.id);
    setEditedData({ ...level });
  };

  const handleSave = async (level: LevelData) => {
    const hasChanges = Object.keys(editedData).some(
      field => editedData[field as keyof LevelData] !== level[field as keyof LevelData]
    );

    if (!hasChanges) {
      setEditingRowId(null);
      setEditedData({});
      return;
    }

    try {
      await setLevelSettings({
        id: editedData.id ?? level.id,
        name: editedData.name ?? level.name,
        description: editedData.description ?? level.description,
        variant: editedData.variant ?? level.variant ?? getDefaultVariant(level.rankName),
        mapId: editedData.mapId ?? level.mapId ?? getDefaultMapId(level.rankName),
      });

      const updatedCurrentLevels = currentLevel.map(l =>
        l.id === editingRowId ? { ...l, ...editedData } : l
      );
      setCurrentLevel(updatedCurrentLevels);

      const updatedAllLevelsData = { ...allLevelsData, [selectedTheme]: updatedCurrentLevels };
      setAllLevelsData(updatedAllLevelsData);

      if (selectedLevel?.id === editingRowId) {
        setSelectedLevel({ ...selectedLevel, ...editedData });
      }

      setEditingRowId(null);
      setEditedData({});

      showToast("success", "Success", "Level settings updated successfully");
    } catch (error) {
      console.error("Error updating level:", error);
      showToast("error", "Error", "Failed to update level settings. Please try again.");
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditedData({});
  };

  const handleFieldChange = (field: keyof LevelData, value: string) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = (level: LevelData) => {
    setSelectedLevel(level);
    setShowPreviewModal(true);
  };

  const handleRowClick = (level: LevelData) => {
    if (editingRowId !== level.id) {
      setSelectedLevel(level);
    }
  };

  return (
    <Panel
      header="Level Settings"
      data-testid="level-settings-panel"
      pt={{ content: { className: "bg-transparent" } }}
    >
      <div className={C(themeClasses.bgElevation2, "flex flex-col gap-6 p-6 rounded-md shadow-md")}>
        <div className="flex justify-start items-center gap-4">
          <div className="flex items-center gap-4">
            <label htmlFor="levelTheme" className="font-semibold text-base min-w-[100px]">
              Level Theme
            </label>
            <Dropdown
              inputId="levelTheme"
              data-testid="level-theme-dropdown"
              value={selectedTheme}
              options={LEVEL_THEME_OPTIONS}
              onChange={handleThemeChange}
              placeholder="Select Theme"
              className="min-w-[200px]"
            />
          </div>
        </div>

        <div
          className={C(
            "border rounded-md overflow-hidden",
            themeClasses.borderSurfaceBorder,
            themeClasses.bgElevation2
          )}
        >
          {/* Table Header */}
          <div
            className={C(
              "flex border-b-2 font-semibold text-color",
              themeClasses.bgElevation1,
              themeClasses.borderSurfaceBorder
            )}
          >
            <div className="flex items-center p-4" style={{ width: "30%" }}>
              Name
            </div>
            <div className="flex items-center p-4" style={{ width: "50%" }}>
              Description
            </div>
            <div className="flex items-center justify-center p-4" style={{ width: "20%" }}>
              Actions
            </div>
          </div>

          {/* Table Body */}
          <div>
            {currentLevel.map((level, index) => {
              const isEditing = editingRowId === level.id;
              const isSelected = selectedLevel?.id === level.id;
              const isStriped = index % 2 === 1;

              return (
                <div
                  key={level.id}
                  data-testid={`level-settings-row-${level.id}`}
                  className={C(
                    "flex border-b border-surface-border transition-colors duration-200 cursor-pointer hover:bg-elevation-1",
                    isSelected
                      ? `${themeClasses.bgElevation1} border-l-4 border-l-primary`
                      : isStriped
                        ? themeClasses.bgElevation3
                        : themeClasses.bgElevation2
                  )}
                  onClick={() => handleRowClick(level)}
                >
                  <div className="flex items-center p-4" style={{ width: "30%" }}>
                    {isEditing ? (
                      <InputText
                        type="text"
                        data-testid="level-name-input"
                        value={editedData.name ?? ""}
                        onChange={e => handleFieldChange("name", e.target.value)}
                        className="w-full p-2"
                      />
                    ) : (
                      <span data-testid="level-name-display">{level.name}</span>
                    )}
                  </div>

                  <div className="flex items-center p-4" style={{ width: "50%" }}>
                    {isEditing ? (
                      <InputTextarea
                        value={editedData.description ?? ""}
                        onChange={e => handleFieldChange("description", e.target.value)}
                        className="w-full p-2"
                        rows={3}
                        autoResize
                      />
                    ) : (
                      <span>{level.description}</span>
                    )}
                  </div>

                  <div
                    className="flex items-center justify-center p-4 gap-2"
                    style={{ width: "20%" }}
                  >
                    {isEditing ? (
                      <>
                        <Button
                          icon="pi pi-check"
                          severity="success"
                          size="small"
                          data-testid="level-save-button"
                          onClick={() => handleSave(level)}
                          tooltip="Save"
                          tooltipOptions={{ position: "top" }}
                        />
                        <Button
                          icon="pi pi-times"
                          severity="danger"
                          size="small"
                          data-testid="level-cancel-button"
                          onClick={handleCancel}
                          tooltip="Cancel"
                          tooltipOptions={{ position: "top" }}
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          icon="pi pi-pencil"
                          text
                          size="small"
                          data-testid="level-edit-button"
                          onClick={() => handleEdit(level)}
                          tooltip="Edit"
                          tooltipOptions={{ position: "top" }}
                        />
                        <Button
                          icon="pi pi-eye"
                          text
                          size="small"
                          data-testid="level-preview-button"
                          onClick={() => handlePreview(level)}
                          tooltip="Preview"
                          tooltipOptions={{ position: "top" }}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog
        header={`Preview: ${selectedLevel?.name || ""}`}
        visible={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        maximizable
        maximized
        modal
        className="w-[90vw] max-w-[1400px]"
        contentClassName="min-h-[70vh] p-0 bg-elevation-2"
      >
        <PathPreview variant={selectedLevel?.variant} map={selectedLevel?.mapId} />
      </Dialog>
    </Panel>
  );
};
